"use client";

/**
 * LeadTagChips — visible tag row for the lead details views.
 *
 * Until now leads.tags only powered the leads-page FILTER and two special
 * badges (screening / unreachable); there was no general display. This chip
 * row makes tags visible where agents actually look, with special styling for
 * the automatic status-history tags ("Was: Sold & Approved", "Was: Do not
 * call" — appended by the leads_tag_status_history DB trigger whenever a lead
 * leaves a meaningful status).
 *
 * Chips are removable (same PATCH the screening badge uses) — history tags
 * included, since tags are user-editable by design.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { History, Tag, X } from "lucide-react";

import api from "@/app/_module/services/api";
import { useToast } from "@/app/_module/hooks/use-toast";

// Internal/system tags that already have their own dedicated badge UI —
// don't double-render them as chips.
const HIDDEN_TAGS = new Set(["screening_detected", "unreachable"]);

interface LeadTagChipsProps {
  leadId: string;
  tags?: string[] | null;
  /** compact = smaller chips for tight headers (dialer panel) */
  compact?: boolean;
  /** viewers / shared leads can't edit */
  readOnly?: boolean;
}

export function LeadTagChips({ leadId, tags, compact = false, readOnly = false }: LeadTagChipsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const removeMutation = useMutation({
    mutationFn: async (tagToRemove: string) => {
      const nextTags = (tags || []).filter((t) => t !== tagToRemove);
      await api.patch({
        url: `/api/leads/${leadId}`,
        body: { tags: nextTags },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead"] });
    },
    onError: () => toast({ title: "Couldn't remove tag", variant: "destructive" }),
  });

  const visible = (tags || []).filter((t) => t && !HIDDEN_TAGS.has(t));
  if (visible.length === 0) return null;

  const pad = compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs";

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((tag) => {
        const isHistory = tag.startsWith("Was: ");
        return (
          <span
            key={tag}
            className={`inline-flex items-center gap-1 rounded-md border font-medium ${pad} ${
              isHistory
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-gray-200 bg-gray-50 text-gray-700"
            }`}
            title={isHistory ? `This lead was previously in the "${tag.slice(5)}" stage` : undefined}
          >
            {isHistory ? <History className="h-3 w-3" /> : <Tag className="h-3 w-3" />}
            <span className="whitespace-nowrap">{tag.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</span>
            {!readOnly && (
              <button
                type="button"
                aria-label={`Remove tag ${tag.replace(/_/g, " ")}`}
                className="opacity-40 hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  removeMutation.mutate(tag);
                }}
                disabled={removeMutation.isPending}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}
